import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, imgUrl } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import NewsletterSignup from "../components/NewsletterSignup";
import HeroSilhouettes from "../components/HeroSilhouettes";
import InstagramWall from "../components/InstagramWall";
import { ArrowUpRight } from "lucide-react";

const Home = () => {
  const { settings } = useSettings();
  const [featured, setFeatured] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/artworks", { params: { featured: true } }).then((r) => setFeatured(r.data));
    if (settings.shop_enabled) {
      api.get("/products").then((r) => setProducts(r.data.slice(0, 3)));
    }
  }, [settings.shop_enabled]);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden px-6 md:px-12 lg:px-20 pt-16 md:pt-24 pb-20 md:pb-32 min-h-[640px] md:min-h-[780px] flex items-center">
        <HeroSilhouettes />
        <div className="relative w-full max-w-4xl mx-auto text-center fade-up">
          {settings.home_hero_eyebrow && (
            <div className="label-eyebrow mb-8 text-xs md:text-sm" data-testid="home-hero-eyebrow">
              {settings.home_hero_eyebrow}
            </div>
          )}
          {settings.home_hero_tagline && (
            <h1
              className="font-serif-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-[#1A1F1B] leading-[1.02]"
              data-testid="home-hero-tagline"
            >
              {settings.home_hero_tagline}
            </h1>
          )}
          {settings.home_hero_description && (
            <p
              className="mt-8 md:mt-10 max-w-2xl mx-auto text-lg md:text-xl text-[#3B4A3F] leading-relaxed"
              data-testid="home-hero-description"
            >
              {settings.home_hero_description}
            </p>
          )}
          <div className="mt-12 md:mt-14 flex flex-wrap gap-5 justify-center">
            <Link
              to="/portfolio"
              className="inline-flex items-center justify-center bg-[#3B4A3F] hover:bg-[#2C382F] text-[#F7F6F2] border border-[#3B4A3F] px-10 py-5 text-sm md:text-base uppercase tracking-[0.22em] font-medium transition-colors"
              data-testid="home-cta-portfolio"
            >
              View Portfolio
            </Link>
            {settings.shop_enabled ? (
              <Link
                to="/shop"
                className="inline-flex items-center justify-center bg-transparent hover:bg-[#3B4A3F]/10 text-[#3B4A3F] border border-[#3B4A3F] px-10 py-5 text-sm md:text-base uppercase tracking-[0.22em] font-medium transition-colors"
                data-testid="home-cta-shop"
              >
                Shop Originals & Prints
              </Link>
            ) : (
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-transparent hover:bg-[#3B4A3F]/10 text-[#3B4A3F] border border-[#3B4A3F] px-10 py-5 text-sm md:text-base uppercase tracking-[0.22em] font-medium transition-colors"
                data-testid="home-cta-contact"
              >
                Inquire
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="marquee" data-testid="home-marquee">
        <div className="marquee-inner">
          <span>Mixed Media</span>
          <span>Acrylics &amp; Oils</span>
          <span>Original Collages</span>
          <span>Fabric &amp; Paper</span>
          <span>Studio Editions</span>
          <span>Mixed Media</span>
          <span>Acrylics &amp; Oils</span>
          <span>Original Collages</span>
          <span>Fabric &amp; Paper</span>
          <span>Studio Editions</span>
        </div>
      </div>

      {/* FEATURED */}
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-32">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="label-eyebrow mb-3">Featured Works</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#1A1F1B]">
              From the studio, lately.
            </h2>
          </div>
          <Link
            to="/portfolio"
            className="hidden md:inline-flex items-center gap-2 nav-link"
            data-testid="home-view-all-portfolio"
          >
            View all <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {featured.slice(0, 3).map((a, idx) => (
            <Link
              key={a.id}
              to="/portfolio"
              className={`art-card group overflow-hidden ${
                idx === 0
                  ? "md:col-span-7 md:row-span-2"
                  : idx === 1
                  ? "md:col-span-5"
                  : "md:col-span-5"
              }`}
              data-testid={`home-featured-${idx}`}
            >
              <div className="overflow-hidden bg-[#EFECE5]">
                <img
                  src={imgUrl(a.image_url)}
                  alt={a.title}
                  className={`w-full object-contain ${
                    idx === 0 ? "aspect-[4/5]" : "aspect-[5/4]"
                  }`}
                />
              </div>
              <div className="mt-4 flex items-baseline justify-between">
                <div>
                  <div className="font-serif-display text-2xl text-[#1A1F1B]">
                    {a.title}
                  </div>
                  <div className="text-xs text-[#849383] mt-1">
                    {a.medium} · {a.year}
                  </div>
                </div>
                <ArrowUpRight size={18} className="text-[#3B4A3F] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* SHOP PREVIEW (only if shop enabled) */}
      {settings.shop_enabled && products.length > 0 && (
        <section className="bg-[#EFECE5] grain border-y border-[#D8D3CC]">
          <div className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
            <div className="grid md:grid-cols-12 gap-12">
              <div className="md:col-span-4">
                <div className="label-eyebrow mb-3">The Shop</div>
                <h2 className="font-serif-display text-4xl md:text-5xl text-[#1A1F1B] leading-tight">
                  Take a piece home.
                </h2>
                <p className="mt-6 text-[#4E5C50] leading-relaxed">
                  Originals on cradled canvas and limited archival prints, made
                  slowly in the studio and shipped with care.
                </p>
                <Link to="/shop" className="btn-primary mt-8 inline-block" data-testid="home-shop-cta">
                  Enter the Shop
                </Link>
              </div>
              <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
                {products.map((p) => (
                  <Link
                    to="/shop"
                    key={p.id}
                    className="art-card group"
                    data-testid={`home-shop-preview-${p.id}`}
                  >
                    <div className="overflow-hidden border border-[#D8D3CC] bg-[#F7F6F2]">
                      <img
                        src={imgUrl(p.image_url)}
                        alt={p.title}
                        className="w-full aspect-[4/5] object-contain"
                      />
                    </div>
                    <div className="mt-3">
                      <div className="font-serif-display text-lg text-[#1A1F1B]">
                        {p.title}
                      </div>
                      <div className="text-xs text-[#849383] uppercase tracking-widest mt-1">
                        {p.kind} · ${p.price.toFixed(0)}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* INSTAGRAM WALL */}
      <InstagramWall />

      {/* NEWSLETTER */}
      <NewsletterSignup source="home_footer" />
    </div>
  );
};

export default Home;
